/**
 * ShopLogic — generador de ofertas de la tienda de cartas entre oleadas, y
 * las acciones económicas asociadas: costo escalado, REFRESH (50 bolas) y
 * LLEVAR TODO (suma de precios, aplica las 3 cartas).
 *
 * Lógica pura sin Phaser: usa `Rng` (core/rng) para todo el azar, así que es
 * completamente determinista y testeable con Vitest.
 */

import type { CardDef, CardOffer, RunState } from '../core/types';
import type { Rng } from '../core/rng';
import { CardEngine } from './CardEngine';
import { rarityTier } from './rarity';

/** Costo fijo (en bolas) de refrescar las 3 ofertas de la tienda. */
export const SHOP_REFRESH_COST = 50;

/** Cuántas ofertas muestra la tienda en cada apertura o refresco. */
export const SHOP_OFFER_COUNT = 3;

/** Crecimiento del costo de una carta por cada oleada transcurrida (12%/oleada). */
const WAVE_COST_GROWTH = 0.12;

export interface GenerateOffersParams {
  /** Pool completo de cartas disponibles (normalmente `src/data/cards.json`). */
  pool: CardDef[];
  /** Oleada actual del run (1-indexada). */
  wave: number;
  /** Número de capítulo actual (1-indexado; 1 = primer capítulo). */
  chapterNumber: number;
  /** Generador determinista, compartido durante todo el run. */
  rng: Rng;
}

/**
 * true si, en este punto del run, las cartas comunes deben ofrecerse gratis
 * (oleadas 1-2 del capítulo 1, según la spec).
 */
export function isFreeCommonWindow(chapterNumber: number, wave: number): boolean {
  return chapterNumber === 1 && wave <= 2;
}

/**
 * Costo final de ofrecer `card` en la oleada/capítulo dados: 0 si cae en la
 * ventana gratis de comunes, o `baseCost` escalado por la oleada actual.
 * Una carta con `baseCost` 0 siempre es gratis (sin importar la oleada).
 */
export function computeOfferCost(card: CardDef, wave: number, chapterNumber: number): number {
  if (card.baseCost <= 0) return 0;
  if (rarityTier(card.rarity) === 'comun' && isFreeCommonWindow(chapterNumber, wave)) {
    return 0;
  }
  const growth = 1 + Math.max(0, wave - 1) * WAVE_COST_GROWTH;
  return Math.round(card.baseCost * growth);
}

/** Peso relativo de cada rareza según la oleada: a mayor oleada, más pesan las cartas raras/épicas. */
function rarityWeight(rarity: CardDef['rarity'], wave: number): number {
  const baseByRarity = [50, 26, 16, 8, 4];
  const base = baseByRarity[rarity - 1] ?? 1;
  const waveFactor = Math.min(Math.max(wave, 1), 20);
  const boost = (rarity - 1) * waveFactor * 1.6;
  return Math.max(1, base + boost);
}

/** Elige `count` elementos de `items` sin repetición, ponderados por `weightOf`. */
function weightedSampleWithoutReplacement<T>(
  rng: Rng,
  items: readonly T[],
  count: number,
  weightOf: (item: T) => number
): T[] {
  const pool = [...items];
  const picked: T[] = [];
  while (picked.length < count && pool.length > 0) {
    const weights = pool.map(weightOf);
    const total = weights.reduce((sum, w) => sum + w, 0);
    let roll = rng.float() * total;
    let index = pool.length - 1;
    for (let i = 0; i < weights.length; i++) {
      roll -= weights[i] as number;
      if (roll <= 0) {
        index = i;
        break;
      }
    }
    picked.push(pool[index] as T);
    pool.splice(index, 1);
  }
  return picked;
}

/**
 * Genera las ofertas de la tienda (hasta `SHOP_OFFER_COUNT`, menos si el
 * pool es más chico), sesgadas por rareza según la oleada actual.
 */
export function generateOffers(params: GenerateOffersParams): CardOffer[] {
  const { pool, wave, chapterNumber, rng } = params;
  const picked = weightedSampleWithoutReplacement(rng, pool, SHOP_OFFER_COUNT, (card) =>
    rarityWeight(card.rarity, wave)
  );
  return picked.map((card) => ({
    cardId: card.id,
    cost: computeOfferCost(card, wave, chapterNumber),
  }));
}

/** Busca la definición de una carta por id en el pool; lanza si no existe (dato corrupto). */
export function resolveCard(pool: readonly CardDef[], cardId: string): CardDef {
  const card = pool.find((c) => c.id === cardId);
  if (!card) {
    throw new Error(`ShopLogic: no existe la carta "${cardId}" en el pool.`);
  }
  return card;
}

/** Suma el costo total de un conjunto de ofertas (lo que cobra LLEVAR TODO). */
export function totalCost(offers: readonly CardOffer[]): number {
  return offers.reduce((sum, offer) => sum + offer.cost, 0);
}

export interface PurchaseResult {
  /** RunState resultante tras pagar y aplicar el/los efecto(s). */
  run: RunState;
  /** Bolas efectivamente gastadas. */
  spent: number;
}

/** Compra UNA oferta: descuenta su costo del contador de bolas y aplica el efecto de la carta. */
export function purchaseOffer(
  run: RunState,
  offer: CardOffer,
  pool: readonly CardDef[]
): PurchaseResult {
  if (offer.cost > run.balls) {
    throw new Error('ShopLogic: no hay bolas suficientes para comprar esta carta.');
  }
  const card = resolveCard(pool, offer.cardId);
  const paidRun: RunState = { ...run, balls: run.balls - offer.cost };
  return { run: CardEngine.apply(card, paidRun), spent: offer.cost };
}

/** LLEVAR TODO: compra las ofertas recibidas por la suma de sus precios y aplica las 3. */
export function purchaseAllOffers(
  run: RunState,
  offers: readonly CardOffer[],
  pool: readonly CardDef[]
): PurchaseResult {
  const cost = totalCost(offers);
  if (cost > run.balls) {
    throw new Error('ShopLogic: no hay bolas suficientes para llevar todo.');
  }
  let current: RunState = { ...run, balls: run.balls - cost };
  for (const offer of offers) {
    const card = resolveCard(pool, offer.cardId);
    current = CardEngine.apply(card, current);
  }
  return { run: current, spent: cost };
}

/** REFRESH: descuenta `SHOP_REFRESH_COST` bolas y genera un nuevo set de ofertas. */
export function refreshOffers(
  run: RunState,
  params: GenerateOffersParams
): { run: RunState; offers: CardOffer[] } {
  if (run.balls < SHOP_REFRESH_COST) {
    throw new Error('ShopLogic: no hay bolas suficientes para refrescar la tienda.');
  }
  const nextRun: RunState = { ...run, balls: run.balls - SHOP_REFRESH_COST };
  return { run: nextRun, offers: generateOffers(params) };
}
